import { host } from '../../app/api';
import { SelectedFeature, VisibleRegion } from '../../app/chatSlice';

const CHAT_URL = `${host}/api/v1/genatator-pipeline/chat`;

export interface ChatRequestBody {
    request_id: string;
    gff_url: string;
    message: string;
    current_state: {
        visible_region: { chromosome: string; start: number; end: number };
        selected_element: { raw: string; is_active: boolean } | null;
    };
}

export type ChatStreamEvent =
    | { type: 'chunk'; content: string }
    | { type: 'done'; history?: { role: string; content: string }[] }
    | { type: 'error'; status?: number; detail: string };

export const buildChatRequest = (
    requestId: string,
    gffUrl: string,
    message: string,
    visibleRegion: VisibleRegion | null,
    selected: SelectedFeature | null,
    isSelectedPanelOpen: boolean
): ChatRequestBody => ({
    request_id: requestId,
    gff_url: gffUrl,
    message,
    current_state: {
        visible_region: visibleRegion ?? { chromosome: '', start: 0, end: 0 },
        selected_element:
            selected && selected.attributeString
                ? { raw: selected.attributeString, is_active: isSelectedPanelOpen }
                : null,
    },
});

/**
 * POST a chat message and stream the SSE response. `onEvent` is invoked
 * for every parsed `data: {...}` frame. Caller is expected to translate
 * those frames into Redux actions.
 */
export const streamChat = async (
    body: ChatRequestBody,
    onEvent: (event: ChatStreamEvent) => void,
    signal?: AbortSignal
): Promise<void> => {
    console.log('[AIChat] -> POST', CHAT_URL, body);
    let response: Response;
    try {
        response = await fetch(CHAT_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            signal,
        });
    } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        console.warn('[AIChat] network error', err);
        onEvent({ type: 'error', detail: (err as Error).message || 'Network error' });
        return;
    }

    console.log('[AIChat] <- response', response.status, response.headers.get('content-type'));
    if (!response.ok || !response.body) {
        onEvent({ type: 'error', status: response.status, detail: `HTTP ${response.status}` });
        return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            let boundary = buffer.indexOf('\n\n');
            while (boundary !== -1) {
                const rawEvent = buffer.slice(0, boundary);
                buffer = buffer.slice(boundary + 2);
                boundary = buffer.indexOf('\n\n');

                for (const line of rawEvent.split('\n')) {
                    const trimmed = line.trim();
                    if (!trimmed.startsWith('data:')) continue;
                    const json = trimmed.slice(5).trim();
                    if (!json) continue;
                    try {
                        const parsed = JSON.parse(json) as ChatStreamEvent;
                        console.log('[AIChat] <- event', parsed);
                        onEvent(parsed);
                    } catch (err) {
                        console.warn('[AIChat] malformed SSE line', json, err);
                    }
                }
            }
        }
    } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        onEvent({ type: 'error', detail: (err as Error).message || 'Stream interrupted' });
    }
};
